import * as React from "react";
import { Alert, Box, Snackbar, Stack, Typography } from "@mui/material";
import { Account, FileTags, UploadsRecord } from "@/types";
import { Header, Subheader } from "@/components/atoms/Header";
import { UploadField } from "@/components/organisms/UploadField";
import { Section } from "@/Sections";
import { Accordion } from "@/components/molecules/Accordion";
import { colors } from "@/styles/constants";
import { Hr } from "@/components/atoms/HR";
import { updateDocuments } from "@/services/api/caseApi";
import { RequiredHeader } from "@/components/molecules/RequiredHeader";
import { NEContext } from "@/store/NonExpressState";
import {
  isWillRequired,
  isProofOfBankAccountRequired,
  isGrantOfRepresentationRequired,
  IDocumentsProps,
  updatedFormFromPersistedState,
  serverRecordFromPersistedState,
  IDocumentsView,
  validateRecord,
  isGOROptional,
} from "@/helpers/documents-helpers";

export const Documents: React.FC<IDocumentsProps> = ({
  caseId,
  signature,
  prepareUpdate,
  updateSuccessful,
  updateFailure,
  form,
  serviceProvidersMap,
  busy,
  continueWithoutChanges,
  remoteError,
  uploadFile,
  uploadedFileInfo,
  persistedState,
  setPersistedState,
  onSectionClick,
  updateTemplate,
  removeRemoteError,
}) => {
  const { record, errors, hasChanges } = persistedState;

  const {
    state: { willExists, intestacyFlow, quilter, notifierRole, spsRequireGOR },
  } = React.useContext(NEContext);

  const askForGrantOfRepresentation = React.useMemo(
    () => isGrantOfRepresentationRequired(quilter, spsRequireGOR),
    [quilter, spsRequireGOR]
  );
  const showAGOROptional = React.useMemo(
    () => isGOROptional(quilter, spsRequireGOR),
    [quilter, spsRequireGOR]
  );
  // console.log({ willExists, intestacyFlow, quilter, notifierRole, spsRequireGOR }, quilter);

  const askForWillAndProbate = React.useMemo(() => {
    return isWillRequired(
      form?.accounts,
      form?.notifier,
      serviceProvidersMap,
      intestacyFlow,
      willExists
    );
  }, [serviceProvidersMap, form.accounts, form.notifier]);

  const askForProofOfBankAccount = React.useMemo(
    () => isProofOfBankAccountRequired(form.accounts, quilter, notifierRole),
    [form.accounts, quilter, notifierRole]
  );
  const askForProofOfAddress = React.useMemo(
    () =>
      form.accounts?.some(
        (account: Account) =>
          account.companyId && serviceProvidersMap[account.companyId]?.proofOfAddressNeeded
      ),
    [serviceProvidersMap, form.accounts]
  );

  const updateProofOfAddress = React.useCallback(
    (proc: (a: UploadsRecord) => UploadsRecord, initial?: boolean) => {
      setPersistedState((s) => ({
        ...s,
        hasChanges: !initial,
        record: { ...s.record, proofOfAddress: proc(s.record.proofOfAddress) },
      }));
    },
    [setPersistedState]
  );

  const updateProofOfDeath = React.useCallback(
    (proc: (a: UploadsRecord) => UploadsRecord, initial?: boolean) => {
      setPersistedState((s) => ({
        ...s,
        hasChanges: !initial,
        record: { ...s.record, proofOfDeath: proc(s.record.proofOfDeath) },
      }));
    },
    [setPersistedState]
  );

  const updateWillAndProbate = React.useCallback(
    (proc: (a: UploadsRecord) => UploadsRecord, initial?: boolean) => {
      setPersistedState((s) => ({
        ...s,
        hasChanges: !initial,
        record: { ...s.record, willAndProbate: proc(s.record.willAndProbate) },
      }));
    },
    [setPersistedState]
  );

  const updateGrantOfRepresentation = React.useCallback(
    (proc: (a: UploadsRecord) => UploadsRecord, initial?: boolean) => {
      setPersistedState((s) => ({
        ...s,
        hasChanges: !initial,
        record: { ...s.record, grantOfRepresentation: proc(s.record.grantOfRepresentation) },
      }));
    },
    [setPersistedState]
  );

  const updateProofOfBankAccount = React.useCallback(
    (proc: (a: UploadsRecord) => UploadsRecord, initial?: boolean) => {
      setPersistedState((s) => ({
        ...s,
        hasChanges: !initial,
        record: { ...s.record, proofOfBankAccount: proc(s.record.proofOfBankAccount) },
      }));
    },
    [setPersistedState]
  );

  const setError = React.useCallback(
    (name: string, message: string) => {
      setPersistedState((s) => ({
        ...s,
        hasChanges: true,
        record: { ...s.record },
        errors: { ...s.errors, [name]: message },
      }));
    },
    [setPersistedState]
  );

  const onContinue = React.useCallback(
    async (justSave?: boolean) => {
      const errors = validateRecord(
        record,
        !!askForProofOfAddress,
        askForWillAndProbate,
        askForProofOfBankAccount,
        askForGrantOfRepresentation
      );

      if (errors) {
        setPersistedState({
          ...persistedState,
          errors,
        });
        return;
      }

      if (!hasChanges) {
        continueWithoutChanges(Section.Documents, justSave);
        return;
      }
      const proofOfAddressUploads = record.proofOfAddress.files.map((file) => {
        return uploadFile(file, new Array<FileTags>(FileTags.ProofOfAddress));
      });

      const proofOfDeathUploads = record.proofOfDeath.files.map((file, index) => {
        return uploadFile(file, new Array<FileTags>(FileTags.ProofOfDeath));
      });

      const willAndProbateUploads = record.willAndProbate.files.map((file, index) => {
        return uploadFile(
          file,
          // ?? and ?. handles situation when user wants to reupload after adding a wrong format file, it might have to
          // be covered for other uploads also
          new Array<FileTags>(FileTags.Will)
        );
      });

      const grantOfRepresentationUploads = record.grantOfRepresentation.files.map((file, index) => {
        return uploadFile(file, new Array<FileTags>(FileTags.GrantOfRepresentation));
      });

      const proofOfBankAccountUploads = record.proofOfBankAccount.files.map((file, index) => {
        return uploadFile(file, new Array<FileTags>(FileTags.ProofOfBankAccount));
      });

      Promise.all([
        Promise.all(proofOfAddressUploads),
        Promise.all(proofOfDeathUploads),
        Promise.all(willAndProbateUploads),
        Promise.all(grantOfRepresentationUploads),
        Promise.all(proofOfBankAccountUploads),
      ]).then(
        ([
          proofOfAddress,
          proofOfDeath,
          willAndProbate,
          grantOfRepresentation,
          proofOfBankAccount,
        ]) => {
          const newIds = {
            proofOfAddress: record.proofOfAddress.currentIds.concat(
              proofOfAddress.map(({ id }) => id)
            ),
            proofOfDeath: record.proofOfDeath.currentIds.concat(proofOfDeath.map(({ id }) => id)),
            willAndProbate: record.willAndProbate.currentIds.concat(
              willAndProbate.map(({ id }) => id)
            ),
            grantOfRepresentation: record.grantOfRepresentation.currentIds.concat(
              grantOfRepresentation.map(({ id }) => id)
            ),
            proofOfBankAccount: record.proofOfBankAccount.currentIds.concat(
              proofOfBankAccount.map(({ id }) => id)
            ),
          };

          let futureForm = updatedFormFromPersistedState(form, newIds);
          futureForm = prepareUpdate(Section.Documents, futureForm);

          if (!futureForm.documents) throw new Error("Unexpected");

          return updateDocuments({
            caseId,
            signature,
            record: serverRecordFromPersistedState(persistedState, newIds),
          }).then(() => {
            setPersistedState({
              ...persistedState,
              hasChanges: false,
              record: {
                ...persistedState.record,
                proofOfAddress: {
                  originalIds: newIds.proofOfAddress,
                  currentIds: newIds.proofOfAddress,
                  previousDocuments: [],
                  files: [],
                },
                proofOfDeath: {
                  originalIds: newIds.proofOfDeath,
                  currentIds: newIds.proofOfDeath,
                  previousDocuments: [],
                  files: [],
                },
                willAndProbate: {
                  originalIds: newIds.willAndProbate,
                  currentIds: newIds.willAndProbate,
                  previousDocuments: [],
                  files: [],
                },
                grantOfRepresentation: {
                  originalIds: newIds.grantOfRepresentation,
                  currentIds: newIds.grantOfRepresentation,
                  previousDocuments: [],
                  files: [],
                },
                proofOfBankAccount: {
                  originalIds: newIds.proofOfBankAccount,
                  currentIds: newIds.proofOfBankAccount,
                  previousDocuments: [],
                  files: [],
                },
              },
              errors: {},
              refreshPreviousDocuments: persistedState.refreshPreviousDocuments + 1,
            });
            updateSuccessful(futureForm, Section.Documents, justSave);
          }, updateFailure);
        }
      );
    },
    [
      caseId,
      form,
      hasChanges,
      persistedState,
      signature,
      updateFailure,
      updateSuccessful,
      continueWithoutChanges,
      prepareUpdate,
      setPersistedState,
      record,
      uploadFile,
      askForProofOfAddress,
      askForWillAndProbate,
      askForProofOfBankAccount,
    ]
  );

  return (
    <DocumentsView
      askForProofOfAddress={askForProofOfAddress}
      askForWillAndProbate={askForWillAndProbate}
      askForProofOfBankAccount={askForProofOfBankAccount}
      askForGrantOfRepresentation={askForGrantOfRepresentation}
      busy={busy}
      record={record}
      errors={errors}
      hasChanges={hasChanges}
      remoteError={remoteError}
      uploadedFileInfo={uploadedFileInfo}
      setError={setError}
      updateProofOfAddress={updateProofOfAddress}
      updateWillAndProbate={updateWillAndProbate}
      updateGrantOfRepresentation={updateGrantOfRepresentation}
      updateProofOfDeath={updateProofOfDeath}
      updateProofOfBankAccount={updateProofOfBankAccount}
      onContinue={onContinue}
      onSectionClick={onSectionClick}
      updateTemplate={updateTemplate}
      removeRemoteError={removeRemoteError}
      showAGOROptional={showAGOROptional}
    />
  );
};

export const DocumentsView: React.FC<IDocumentsView> = ({
  askForProofOfAddress,
  askForWillAndProbate,
  askForGrantOfRepresentation,
  askForProofOfBankAccount,
  busy,
  record,
  errors,
  hasChanges,
  remoteError,
  uploadedFileInfo,
  setError,
  updateProofOfAddress,
  updateWillAndProbate,
  updateGrantOfRepresentation,
  updateProofOfDeath,
  updateProofOfBankAccount,
  onContinue,
  onSectionClick,
  updateTemplate,
  removeRemoteError,
  showAGOROptional,
}) => {
  React.useEffect(() => {
    updateTemplate({
      busy,
      currentSection: "documents",
      onNext: () => onContinue(false),
      onSave: hasChanges ? () => onContinue(true) : undefined,
      onSectionClick,
    });
  }, [busy, onContinue, onSectionClick, updateTemplate, hasChanges]);

  return (
    <Stack rowGap={4}>
      <Stack rowGap={2}>
        <Header level={1}>Time to add your documents.</Header>
        <Box>
          <Subheader faded>Top tip</Subheader>
          <Typography variant="body1">
            When uploading photographs or scans of documents, please make sure that the image
            contains <b>all four corners of the page</b> and that{" "}
            <b>all text is clear and legible</b>.
          </Typography>
        </Box>
      </Stack>
      <Hr />

      <Stack rowGap={2}>
        <Header color={colors.lightTeal} level={2}>
          {" "}
          Your documents{" "}
        </Header>
        <Accordion
          teal
          expanded={record.proofOfDeath.files.length !== 0}
          title={
            <RequiredHeader
              showError={!!errors.proofOfDeath || record.proofOfDeath.currentIds.length === 0}
              title="Death certificate"
            />
          }
        >
          <Stack rowGap={1}>
            <UploadField
              uploadedFileInfo={uploadedFileInfo}
              busy={false}
              record={record.proofOfDeath}
              update={updateProofOfDeath}
              error={errors.proofOfDeath}
              setError={(error: any) => setError("proofOfDeath", error)}
            />
            <Typography variant="body1">
              By uploading a file, you are confirming that you certify that it is a <b>true</b> and
              {"  "}
              <b>correct</b> copy of the death certificate.
            </Typography>
            <Typography variant="body1">
              {" "}
              Please upload the death certificate. If a death certificate is not available, please
              upload the <b>Coroner’s Interim Certificate</b>.
            </Typography>
            <Typography variant="body1">
              Some companies may require a physical copy to be sent instead of a certified copy.
            </Typography>
          </Stack>
        </Accordion>

        {askForProofOfAddress && (
          <Accordion
            teal
            expanded={record.proofOfAddress.files.length !== 0}
            title={
              <RequiredHeader
                showError={!!errors.proofOfAddress || record.proofOfAddress.currentIds.length === 0}
                title="Proof of your address"
              />
            }
          >
            <Stack rowGap={1}>
              {" "}
              <UploadField
                uploadedFileInfo={uploadedFileInfo}
                busy={false}
                record={record.proofOfAddress}
                update={updateProofOfAddress}
                error={errors.proofOfAddress}
                setError={(error: any) => setError("proofOfAddress", error)}
              />
              <Typography variant="body1">
                Please upload a <b>Council Tax Bill/Demand Letter</b> (issued within the last 12
                months) <b>OR</b> a <b>Utility Bill</b> (issued within the last 3 months.)
              </Typography>
              <div>
                <Typography variant="body1">
                  If you don’t have either of these documents, please upload one of the following:
                </Typography>

                <ul style={{ margin: 0 }}>
                  <li>
                    A Bank, Building Society or Credit Card Statement (issued within the last 3
                    months)
                  </li>
                  <li>A Tenancy Agreement (issued within the last 12 months)</li>
                </ul>

                <p>
                  Some companies may require a physical copy to be sent instead of a certified copy.
                </p>
              </div>
            </Stack>
          </Accordion>
        )}

        <Accordion
          teal
          expanded={record.willAndProbate.files.length !== 0}
          title={
            <RequiredHeader
              optional={!askForWillAndProbate}
              showError={!!errors.willAndProbate || record.willAndProbate.currentIds.length === 0}
              title="The Will"
            />
          }
        >
          <Stack rowGap={1}>
            {" "}
            <UploadField
              uploadedFileInfo={uploadedFileInfo}
              busy={false}
              record={record.willAndProbate}
              update={updateWillAndProbate}
              error={errors.willAndProbate}
              setError={(error: any) => setError("willAndProbate", error)}
            />
            <Stack rowGap={2}>
              <Typography variant="body1">
                If the person who died wrote a will, please upload images, scans or copies of each{" "}
                <b> of the pages below </b>. <b>Please do NOT </b>remove the staples in your
                original Will. Doing so{" "}
                <b>
                  {" "}
                  could cause serious issues and raise questions as to the validity of your Will.{" "}
                </b>
              </Typography>

              <ol style={{ margin: 0 }}>
                <li>the title page</li>
                <li>the page with the Executor details</li>
                <li>the signature page</li>
              </ol>

              <Typography variant="body1">
                {" "}
                Please note that financial institutions, such as organisations handling investments,
                will require a copy of the will.{" "}
              </Typography>

              <Typography variant="body1">
                Some companies may require a physical copy to be sent instead of a certified copy.
              </Typography>
            </Stack>
          </Stack>
        </Accordion>

        {(askForGrantOfRepresentation || showAGOROptional) && (
          <Accordion
            expanded={record.grantOfRepresentation.files.length !== 0}
            teal
            title={
              <RequiredHeader
                optional={showAGOROptional}
                showError={
                  !!errors.grantOfRepresentation ||
                  record.grantOfRepresentation.currentIds.length === 0
                }
                title="Grant of Representation"
              />
            }
          >
            {" "}
            <Stack rowGap={1}>
              <UploadField
                uploadedFileInfo={uploadedFileInfo}
                busy={false}
                record={record.grantOfRepresentation}
                update={updateGrantOfRepresentation}
                error={errors.grantOfRepresentation}
                setError={(error: any) => setError("grantOfRepresentation", error)}
              />

              <Typography variant="body1">
                If you have received a Grant of Representation (<b>Grant of Probate</b>,{" "}
                <b>Letters of Administration</b> or <b>Certificate of Confirmation</b>) please
                upload a scan of the <b>certified copy</b>. Please note that all Executors - where
                applicable - will be required to sign any instruction.Some providers may allow a
                physical copy to be sent instead of a certified copy.
              </Typography>
            </Stack>
          </Accordion>
        )}
        {askForProofOfBankAccount && (
          <Accordion
            expanded={record.proofOfBankAccount.files.length !== 0}
            teal
            title={
              <RequiredHeader
                showError={
                  !!errors.proofOfBankAccount || record.proofOfBankAccount.currentIds.length === 0
                }
                title="Proof of a Bank Account"
              />
            }
          >
            {" "}
            <Stack rowGap={1}>
              <UploadField
                uploadedFileInfo={uploadedFileInfo}
                busy={false}
                record={record.proofOfBankAccount}
                update={updateProofOfBankAccount}
                error={errors.proofOfBankAccount}
                setError={(error: any) => setError("proofOfBankAccount", error)}
              />

              <Typography variant="body1">
                Please upload a<b> void cheque </b> or <b>bank statement</b>, if taking a withdrawal
                to a personal bank account
              </Typography>
            </Stack>
          </Accordion>
        )}
      </Stack>

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteError}
        autoHideDuration={6000}
        onClose={removeRemoteError}
      >
        <Alert elevation={6} variant="filled" severity="error" onClose={removeRemoteError}>
          {remoteError}
        </Alert>
      </Snackbar>
    </Stack>
  );
};
